import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Users } from 'lucide-react';

export const MeetingSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quotaTotal, setQuotaTotal] = useState(30);
  const [finalizeIds, setFinalizeIds] = useState([]);
  const [members, setMembers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([api.getMeetingSettings(), api.listTeamMembers()]);
      setQuotaTotal(s.data.quota_total);
      setFinalizeIds(s.data.finalize_user_ids || []);
      setMembers((m.data || []).filter(u => u.role !== 'ac_member'));
    } catch { toast.error('Failed to load meeting settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleUser = (id) =>
    setFinalizeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.saveMeetingSettings({
        quota_total: Number(quotaTotal),
        finalize_user_ids: finalizeIds,
      });
      setQuotaTotal(res.data.quota_total);
      toast.success('Meeting settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="meeting-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-blue-400" />
        <h3 className="font-heading text-base text-foreground">Team Meeting</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Quotas per meeting and who may finalize a meeting (admins always can).
      </p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Max deals / meeting</p>
              <Input type="number" min={1} max={200} value={quotaTotal}
                onChange={e => setQuotaTotal(e.target.value)}
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground w-28"
                data-testid="meeting-quota-total-input" />
            </div>
          </div>
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">May finalize meetings (besides admins)</p>
            <div className="space-y-1.5">
              {members.map(m => (
                <label key={m.id} className="flex items-center gap-2 cursor-pointer" data-testid={`finalize-user-${m.id}`}>
                  <Checkbox checked={finalizeIds.includes(m.id)} onCheckedChange={() => toggleUser(m.id)} />
                  <span className="text-sm text-foreground">{m.name}</span>
                  <span className="text-[10px] text-muted-foreground uppercase">{m.role}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="meeting-settings-save-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save
          </Button>
        </div>
      )}
    </div>
  );
};
